import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { defaultItemDisplayStringFunction } from "abreact/incubator/ui/form/AbInputUtils";
import { BaseMachine } from "abreact/incubator/ui/machine/BaseMachine";
import { SelectionManager } from "abreact/incubator/ui/manager/SelectionManager";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { action, computed, observable, observe, makeObservable } from "mobx";
import { SEPARATOR } from "abreact/input/AbList";

/**
 * A cached representation of an item and its rendered string.
 */

export let AbListMachine = (_class = class AbListMachine extends BaseMachine {
  constructor(props) {
    super();
    _defineProperty(this, "selectionManager", void 0);
    _defineProperty(this, "itemDisplayStringFunction", void 0);
    _defineProperty(this, "valuesEqual", void 0);
    _initializerDefineProperty(this, "currentText", _descriptor, this);
    _defineProperty(this, "renderedItems", observable.array([], {
      deep: false
    }));
    makeObservable(this);
    this.currentText = "";
    if (props.selectionManager) {
      this.selectionManager = props.selectionManager;
    } else {
      this.selectionManager = new SelectionManager({
        selectionMode: props ? props.selectionMode : undefined
      });
    }
    const itemDisplayStringFunction = props.itemDisplayStringFunction || defaultItemDisplayStringFunction;
    this.itemDisplayStringFunction = item => {
      let displayString = itemDisplayStringFunction(item);
      if (displayString === undefined || displayString === null) {
        displayString = defaultItemDisplayStringFunction(item);
        if (displayString === undefined || displayString === null) {
          throw new Error(`Item display string function returned non-string value ${displayString}`);
        }
      }
      return displayString;
    };
    if (props.items) {
      this.items = props.items;
    }
    if (props.valuesEqual) {
      this.valuesEqual = props.valuesEqual;
    } else {
      this.valuesEqual = (a, b) => a === b;
    }
  }
  get selectionMode() {
    return this.selectionManager.selectionMode;
  }
  set selectionMode(mode) {
    this.selectionManager.selectionMode = mode;
  }
  canSelect(item) {
    return true;
  }
  get items() {
    return this.renderedItems.map(renderedItem => renderedItem.item);
  }
  set items(items) {
    const renderedItems = items.map((item, index) => {
      let displayString = this.itemDisplayStringFunction(item);
      return {
        item: item,
        index: index,
        lowerCaseDisplayString: displayString.toLocaleLowerCase()
      };
    });
    this.renderedItems.replace(renderedItems);
  }

  /**
   * Selects the next item given the current selection.
   *
   *  @returns {boolean} Returns true if the selection was changed.
   */
  selectNextItem() {
    const items = this.items;
    const selectedIndex = this.indexOf(items, this.selectedItem);
    let nextIndex = selectedIndex !== undefined ? selectedIndex + 1 : 0;
    while (nextIndex < items.length) {
      this.selectedItem = items[nextIndex];
      if (this.selectedItem === SEPARATOR) {
        nextIndex++;
      } else {
        return true;
      }
    }
    return false;
  }

  /**
   * Selects the previous item given the current selection.
   *
   *  @returns {boolean} Returns true if the selection was changed.
   */
  selectPreviousItem() {
    const items = this.items;
    let selectedIndex = this.indexOf(items, this.selectedItem);
    while (selectedIndex !== undefined && selectedIndex > 0) {
      this.selectedItem = items[selectedIndex - 1];
      if (this.selectedItem === SEPARATOR) {
        selectedIndex--;
      } else {
        return true;
      }
    }
    return false;
  }
  onSelected(item) {
    if (this.indexOf(this.selectedItems, item) === -1) {
      this.selectionManager.onSelected(item);
    }
  }
  onDeselected(item) {
    if (this.indexOf(this.selectedItems, item) >= 0) {
      this.selectionManager.onDeselected(item);
    }
  }
  toggleSelect(item) {
    this.selectionManager.toggleSelect(item);
  }
  get selectedItem() {
    return this.selectionManager.selectedItem;
  }
  set selectedItem(item) {
    this.selectionManager.selectedItem = item;
  }
  get selectedItems() {
    return this.selectionManager.selectedItems;
  }
  set selectedItems(items) {
    if (!ArrayUtils.hasSameContents(items, this.selectedItems)) {
      this.selectionManager.selectedItems = items;
    }
  }
  clear() {
    this.selectionManager.clear();
  }
  clearCachedItems() {
    this.renderedItems.clear();
  }
  observeItems(onItemsChange) {
    this.registerDestroyListener(observe(this.renderedItems, change => onItemsChange()));
  }
  observeSelectedItems(onSelectionChange) {
    return this.registerDestroyListener(this.selectionManager.observeSelectedItems(onSelectionChange));
  }
  getMatchingItems(searchTerm) {
    return this.renderedItems.filter(item => {
      return item.lowerCaseDisplayString.indexOf(searchTerm) >= 0;
    });
  }
  onKeyDown(key) {
    if (key === KeyValue.ARROW_UP) {
      if (this.selectionMode === SelectionMode.SINGLE) {
        this.selectPreviousItem();
      }
      return true;
    } else if (key === KeyValue.ARROW_DOWN) {
      if (this.selectionMode === SelectionMode.SINGLE) {
        this.selectNextItem();
      }
      return true;
    }
    return false;
  }
  onKeyPress(key) {
    if (key.length === 1) {
      // First try adding the key to the current search term...
      let searchTerm = (this.currentText + key).toLocaleLowerCase();
      let matchingItems = this.getMatchingItems(searchTerm);
      // If there are no matches, start a new search term consisting of just the key
      if (matchingItems.length === 0) {
        searchTerm = key.toLocaleLowerCase();
        matchingItems = this.getMatchingItems(searchTerm);
      }
      if (matchingItems.length > 0) {
        this.currentText = searchTerm;
        this.selectedItem = matchingItems[0].item;
      } else {
        this.currentText = "";
      }
      return true;
    }
    return false;
  }
  indexOf(array, item) {
    return ArrayUtils.firstIndex(array, x => {
      return this.valuesEqual(item, x);
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "currentText", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "items", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "items"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectNextItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectNextItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectPreviousItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectPreviousItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onSelected", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onSelected"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDeselected", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDeselected"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleSelect", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleSelect"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedItem", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedItems", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedItems"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearCachedItems", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearCachedItems"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onKeyDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onKeyDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onKeyPress", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onKeyPress"), _class.prototype), _class);
//# sourceMappingURL=AbListMachine.js.map
